import { useMemo } from 'react';
import { useCatalogMultiSelectionContext } from 'src/contexts/CatalogMultiSelectionContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import {
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { unpackNumericIds } from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { useSearchDataContextWithCatalogFallback } from 'src/contexts/SearchDataContext';
import {
  ActionOutboxEntityType,
  EventWithData,
  InventoryViewMode,
  ListingQuery,
  PurchaseOrderQuery,
  SaleQuery,
} from 'src/WebApiController';

const sortEvents = (a: EventWithData, b: EventWithData) => {
  const aStart = a.event.dates.start
    ? new Date(a.event.dates.start)
    : new Date();
  const bStart = b.event.dates.start
    ? new Date(b.event.dates.start)
    : new Date();
  return aStart < bStart ? -1 : aStart === bStart ? 0 : 1;
};

export type FilterQueryType = ListingQuery | SaleQuery | PurchaseOrderQuery;

export type UseFilterQueryStateType<T extends FilterQueryType> = T & {
  eventOrMappingIds?: string[] | null;
  purchaseMonths?: string[];
};

type UseFilterQueryState<T extends FilterQueryType> = {
  filterQueryWithEventIds: UseFilterQueryStateType<T>;
  /**
   * Returns a list of selected viagogoIds grouped by event
   */
  selectedViagogoVirtualIds: string[] | undefined;
};

export const useFilterQueryState = <T extends FilterQueryType>(
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase,
  // All the group ids cannot be outside of this one
  groupIdSuperSet?: string[],
  useTicketGroupIds?: boolean
): UseFilterQueryState<T> => {
  const { flattenedIds, unfilteredFlattenedIds } =
    useCatalogMultiSelectionContext();
  const { getSelection, isAllSelected } = useMultiSelectionContext();
  const selectionState = getSelection();

  const { filterQuery } = useFilterQueryContext<T>();
  const { eventsTransformed } = useSearchDataContextWithCatalogFallback();
  const { timePeriodPurchasesQuery } = usePurchaseDataContext();

  const isFlattenedViewWithNoGroupId = useMemo(() => {
    const { groupIds, items } = selectionState;
    return (
      filterQuery.viewMode === InventoryViewMode.FlattenedView &&
      ((groupIds.length === 1 && groupIds[0] === NO_GROUP_ID) ||
        (items.groupIds.length === 1 && items.groupIds[0] === NO_GROUP_ID))
    );
  }, [filterQuery.viewMode, selectionState]);

  const filterQueryWithEventIds = useMemo<UseFilterQueryStateType<T>>(() => {
    const { groupIds, items } = selectionState;

    // Clean up the oldPosEventIds for filtering, which is only used for the event inventory page
    if (entityType === ActionOutboxEntityType.Listing) {
      Object.assign(filterQuery as ListingQuery, { oldPosEventIds: null });
    }

    // Flattened view, have to pick events from listings
    if (isFlattenedViewWithNoGroupId) {
      if (
        isAllSelected &&
        flattenedIds?.length === unfilteredFlattenedIds?.length
      ) {
        const allEventMappingIds =
          eventsTransformed
            ?.map((ev) => ev.event.virtId)
            ?.filter(
              (id) =>
                id !== NO_GROUP_ID &&
                (!groupIdSuperSet?.length || groupIdSuperSet.includes(id))
            ) ?? [];

        if (entityType === ActionOutboxEntityType.Purchase) {
          return {
            ...filterQuery,
            purchaseMonths: timePeriodPurchasesQuery.data?.map(
              (m) => m.firstOfTimePeriod
            ),
            eventOrMappingIds: allEventMappingIds,
          };
        }

        return {
          ...filterQuery,
          eventOrMappingIds: allEventMappingIds,
        };
      } else {
        // Item selections only
        const entityIds = selectionState.items.itemIds;
        return {
          ...filterQuery,
          eventOrMappingIds: [],
          entityIds: entityIds,
        };
      }
    }

    // Non-flattened view

    // Filter the group ids to the super-set so we don't accidentally include more items than this was meant for
    const filteredGroupIds = !groupIdSuperSet?.length
      ? groupIds
      : groupIds.filter((id) => groupIdSuperSet.includes(id));

    if (entityType === ActionOutboxEntityType.Purchase) {
      const unpackedPurchaseIds = new Set<number>();
      const unpackedTicketGroupIds = new Set<number>();
      if (useTicketGroupIds) {
        items.itemIds.forEach((packedId) => {
          const unpackedIds = unpackNumericIds(packedId);
          if (unpackedIds[0]) {
            unpackedPurchaseIds.add(unpackedIds[0]);
          }
          if (unpackedIds[1]) {
            unpackedTicketGroupIds.add(unpackedIds[1]);
          }
        });
      }

      const entityIds: number[] | null = useTicketGroupIds
        ? Array.from(unpackedPurchaseIds)
        : items.itemIds.map((id) => parseInt(id, 10));

      const ticketGroupIds: number[] | null = useTicketGroupIds
        ? Array.from(unpackedTicketGroupIds)
        : null;

      const allEventMappingIds =
        eventsTransformed
          ?.map((ev) => ev.event.virtId)
          ?.filter(
            (id) =>
              id !== NO_GROUP_ID &&
              (!groupIdSuperSet?.length ||
                !useTicketGroupIds ||
                groupIdSuperSet.includes(id))
          ) ?? [];

      return {
        ...filterQuery,
        // If it's a purchase query, use the purchase months
        purchaseMonths: useTicketGroupIds ? undefined : filteredGroupIds,
        eventOrMappingIds: useTicketGroupIds
          ? filteredGroupIds
          : allEventMappingIds,
        entityIds: entityIds,
        ticketGroupIds: ticketGroupIds,
      };
    }

    if (eventsTransformed == null) {
      return {
        ...filterQuery,
        eventOrMappingIds: filteredGroupIds,
        entityIds: items.itemIds.map((id) => parseInt(id, 10)),
      };
    }

    // We want to make sure we get all the events sharing the same group ids because a single gropu id can be part of a merged event
    const normalizedGroupIdsByMergedEvents = eventsTransformed
      ?.filter((ev) => groupIds.includes(ev.event.virtId))
      ?.map((ev) => ev.event.virtId)
      ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));

    return {
      ...filterQuery,
      // Get all the event ids that matches the viagogo ids in groupIdsAsViagogoIds
      eventOrMappingIds: normalizedGroupIdsByMergedEvents,
      entityIds: items.itemIds.map((id) => parseInt(id, 10)),
    };
  }, [
    selectionState,
    entityType,
    isFlattenedViewWithNoGroupId,
    groupIdSuperSet,
    eventsTransformed,
    filterQuery,
    isAllSelected,
    flattenedIds?.length,
    unfilteredFlattenedIds?.length,
    timePeriodPurchasesQuery.data,
    useTicketGroupIds,
  ]);

  // This is used for the Catchup
  const selectedViagogoVirtualIds = useMemo<string[] | undefined>(() => {
    const { groupIds, items } = selectionState;

    // Flattened view, have to pick events from listings
    if (isFlattenedViewWithNoGroupId) {
      if (isAllSelected) {
        return eventsTransformed
          ?.toSorted(sortEvents)
          ?.map((ev) => ev.event.virtId);
      } else {
        const entityIds = items.itemIds;
        return eventsTransformed
          ?.filter((ev) =>
            entityType === ActionOutboxEntityType.Listing
              ? ev.entities.listings?.some((l) =>
                  entityIds.includes(l.id.toString())
                )
              : ev.entities.sales?.some((s) =>
                  entityIds.includes(s.id.toString())
                )
          )
          ?.toSorted(sortEvents)
          ?.map((ev) => ev.event.virtId)
          ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));
      }
    }

    // If the selection has events, use it
    if (groupIds?.length) {
      if (entityType === ActionOutboxEntityType.Purchase) {
        return;
      }

      return eventsTransformed
        ?.filter((ev) => groupIds.includes(ev.event.virtId))
        ?.toSorted(sortEvents)
        ?.map((ev) => ev.event.virtId)
        ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));
    }

    return;
  }, [
    entityType,
    eventsTransformed,
    isAllSelected,
    isFlattenedViewWithNoGroupId,
    selectionState,
  ]);

  return { filterQueryWithEventIds, selectedViagogoVirtualIds };
};
